import type { Feed, MediaItem, SharedData } from '@/types/feed.type'

import moment from 'moment'
import { toast } from 'sonner'
import { useState } from 'react'
import { sleep } from '@/utils/sleep'
import { paths } from '@/routes/paths'
import { Role } from '@/types/user.type'
import { Card } from '@/components/ui/card'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routes/hooks/use-router'
import { Dialog, DialogTrigger } from '@/components/ui/dialog'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LightboxDialog from '@/components/share-components/lightbox-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { MessageCircle, Heart, Share2, Laugh, Frown, Angry } from 'lucide-react'
import ReactionsListDialog from '@/components/share-components/reactions-list-dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  ThumbsUp,
  StarIcon,
  EllipsisVertical,
  Trash2,
  Pencil,
} from 'lucide-react'
import {
  ReactionType,
  type ReactToFeed,
  type ReactionCounts,
} from '@/types/feed.type'

import FeedSharedContent from './feed-shared-content'
import FeedShareFormDialog from './feed-share-form-dialog'

export default function FeedCard({
  feed,
  feedId,
  getFeedById,
  reload,
}: {
  feed: Feed
  feedId?: string
  getFeedById?: (id: string) => Promise<void>
  reload?: VoidFunction
}) {
  const { user } = useAuthContext()
  const router = useRouter()

  const [reactionOpen, setReactionOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [reacting, setReacting] = useState<ReactionType | null>(null)

  const [shareId, setShareId] = useState<string | null>(null)
  // modal state
  const [open, setOpen] = useState(false)

  // const [openEdit, setOpenEdit] = useState(false)
  // console.log('openEdit', openEdit)
  // เพิ่ม state สำหรับ confirm modal
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [SelectedFeedId, setSelectedFeedId] = useState<string | null>(null)

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Early return if feed or feed.user is not available
  if (!feed?.user) {
    return null
  }

  const { imageUrl, displayName, firstName, lastName, email, role } = feed.user
  const userName =
    firstName && lastName ? `${firstName} ${lastName}` : displayName || email

  const mediaItems: MediaItem[] =
    (feed.mediaItems as MediaItem[] | undefined) || []

  // สร้าง array ของ reactions ที่มีค่ามากกว่า 0
  const activeReactions = Object.entries(feed.reactionCounts || {})
    .filter(([key, count]) => key !== 'total' && count > 0)
    .sort((a, b) => b[1] - a[1]) // เรียงจากมากไปน้อย
    .slice(0, 3) // แสดงแค่ 3 อันแรก
    .map(([type]) => type as keyof Omit<ReactionCounts, 'total'>)

  // Map reaction type to icon component
  // ฟังก์ชันนี้จะเช็คว่า currentUserReaction ตรงกับ type หรือไม่ ถ้าตรงจะใช้ icon แบบ filled
  const getReactionIcon = (
    type: keyof Omit<ReactionCounts, 'total'>,
    currentUserReaction?: ReactionType,
  ) => {
    const iconProps = { className: 'w-5 h-5', strokeWidth: 2 }
    const isActive = currentUserReaction?.toLowerCase() === type

    switch (type) {
      case 'like':
        return isActive ? (
          <ThumbsUp
            fill="currentColor"
            {...iconProps}
            className="text-blue-400"
          />
        ) : (
          <ThumbsUp {...iconProps} className="text-blue-400" />
        )
      case 'love':
        return isActive ? (
          <Heart fill="currentColor" {...iconProps} className="text-pink-500" />
        ) : (
          <Heart {...iconProps} className="text-pink-500" />
        )
      case 'haha':
        return isActive ? (
          <Laugh fill="#ffe278" {...iconProps} className="text-yellow-700" />
        ) : (
          <Laugh {...iconProps} className="text-yellow-400" />
        )
      case 'wow':
        return isActive ? (
          <StarIcon
            fill="currentColor"
            {...iconProps}
            className="text-green-400"
          />
        ) : (
          <StarIcon {...iconProps} className="text-green-400" />
        )
      case 'sad':
        return isActive ? (
          <Frown fill="#8EC5FF" {...iconProps} className="text-blue-800" />
        ) : (
          <Frown {...iconProps} className="text-blue-300" />
        )
      case 'angry':
        return isActive ? (
          <Angry fill="#ff9a52" {...iconProps} className="text-orange-800" />
        ) : (
          <Angry {...iconProps} className="text-orange-500" />
        )
      default:
        return <ThumbsUp {...iconProps} className="text-blue-400" />
    }
  }

  async function handleReact(feedId: string, reaction: ReactionType) {
    setReacting(reaction)
    try {
      if (!feed.currentUserReaction) {
        await axios.post(endpoints.feeds.reactById(feedId), {
          reactionType: reaction,
        } as ReactToFeed)
      } else if (
        !!feed.currentUserReaction &&
        feed.currentUserReaction !== reaction
      ) {
        await axios.delete(endpoints.feeds.reactById(feedId))
        await sleep(100)
        await axios.post(endpoints.feeds.reactById(feedId), {
          reactionType: reaction,
        } as ReactToFeed)
      } else {
        await axios.delete(endpoints.feeds.reactById(feedId))
      }

      if (getFeedById) await getFeedById(feedId)
    } catch {
      // handle error
    } finally {
      setReacting(null)
    }
  }

  const reactions = [
    {
      type: ReactionType.LIKE,
      label: 'ถูกใจ',
      icon: getReactionIcon('like', feed.currentUserReaction),
    },
    {
      type: ReactionType.LOVE,
      label: 'รักเลย',
      icon: getReactionIcon('love', feed.currentUserReaction),
    },
    {
      type: ReactionType.HAHA,
      label: 'ขำ',
      icon: getReactionIcon('haha', feed.currentUserReaction),
    },
    {
      type: ReactionType.WOW,
      label: 'ว้าว',
      icon: getReactionIcon('wow', feed.currentUserReaction),
    },
    {
      type: ReactionType.SAD,
      label: 'เศร้า',
      icon: getReactionIcon('sad', feed.currentUserReaction),
    },
    {
      type: ReactionType.ANGRY,
      label: 'โกรธ',
      icon: getReactionIcon('angry', feed.currentUserReaction),
    },
  ]

  return (
    <>
      <Card className="mb-4 p-4 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer relative">
        {!!user && (user.role === Role.ADMIN || feed.userId === user?.uid) && (
          <div className="absolute top-3 right-2 flex items-center gap-1 text-xs text-gray-500">
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <EllipsisVertical className="size-4 text-gray-500" />
              </PopoverTrigger>

              <PopoverContent
                align="start"
                className="max-w-[140px] mr-2 flex flex-col gap-2 p-2 bg-white border border-gray-200 rounded-lg shadow-lg"
              >
                {/* ปุ่มแก้ไขโพสต์ */}
                {(user.role !== Role.ADMIN || feed.userId === user?.uid) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setMenuOpen(false)
                      router.push(paths.feedEdit(feed.id))
                    }}
                  >
                    <Pencil className="w-4 h-4 text-blue-500" />
                    แก้ไขโพสต์
                  </Button>
                )}
                {/* ปุ่มลบโพสต์ */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start text-red-500 hover:bg-red-50"
                  onClick={() => {
                    setMenuOpen(false)
                    setConfirmDeleteOpen(true)
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  ลบโพสต์
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        )}

        <div className="flex items-center mb-2">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-2">
            {/* user image */}
            <Avatar className="size-8 ring-1 ring-gray-200">
              <AvatarImage
                src={role !== 'ADMIN' ? imageUrl : '/images/rfc-logo.png'}
                alt={role !== 'ADMIN' ? userName || '' : 'RFC Club'}
                className="object-cover"
              />
              {userName && (
                <AvatarFallback className="size-8 bg-gray-100 text-gray-600">
                  {role !== 'ADMIN' ? userName.slice(0, 2) || '' : 'RFC'}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div>
            <div className="font-medium text-sm text-gray-900">{userName}</div>
            <div className="text-xs text-gray-500">
              {new Date(feed.createdAt).toLocaleString('th-TH', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </div>
          </div>
        </div>
        <div className="mb-2">
          <div
            className="whitespace-pre-line text-base text-gray-800"
            onClick={() => {
              if (!feedId) router.push(paths.feedDetail(feed.id))
            }}
            // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{ __html: feed.content }}
          />

          {/* Shared Content */}
          {!!feed.sharedContent?.contentId &&
            (feed.sharedContent.type === 'livestream'
              ? true
              : !!feed.sharedContent?.data) && (
              <>
                {(() => {
                  let data: SharedData
                  if (feed.sharedContent.type === 'livestream') {
                    const {
                      contentId,
                      originalAuthor,
                      type,
                      description,
                      originalUrl,
                      previewImageUrl,
                    } = feed.sharedContent

                    // แปลง feed.createdAt (Date) เป็น { _seconds: number, _nanoseconds: number }
                    // ใช้ moment ในการแปลงวันที่

                    const createdAtMoment = moment(feed.createdAt)
                    const time = {
                      _seconds: createdAtMoment.unix(),
                      _nanoseconds: createdAtMoment.milliseconds() * 1_000_000,
                    }

                    data = {
                      id: contentId,
                      userId: originalAuthor?.userId || '',
                      userName: originalAuthor?.displayName || '',
                      firstName: '',
                      lastName: '',
                      userImageUrl: originalAuthor?.imageUrl || '',
                      type,
                      content: description || '',
                      mediaItems: [
                        {
                          type: 'image',
                          url: originalUrl || '',
                          thumbnailUrl: previewImageUrl,
                        },
                      ],
                      status: feed.sharedContent.data?.status || '',
                      privacy: '',
                      likeCount: 0,
                      commentCount: 0,
                      shareCount: 0,
                      createdAt: time,
                      role: feed.sharedContent.data?.originalContentUserRole,
                    }
                  } else {
                    if (!feed.sharedContent.data) {
                      return null
                    }
                    data = feed.sharedContent.data
                  }

                  return (
                    <FeedSharedContent
                      isProgram={feed.sharedContent.type === 'livestream'}
                      contentId={feed.sharedContent.contentId}
                      sharedData={data}
                    />
                  )
                })()}
              </>
            )}

          {/* MEDIA GALLERY */}
          {mediaItems.length > 0 && (
            <div
              className={`mt-3 grid gap-2
              ${
                mediaItems.length === 1
                  ? 'grid-cols-1'
                  : mediaItems.length === 3
                    ? 'grid-cols-2'
                    : 'grid-cols-2'
              }`}
            >
              {mediaItems.slice(0, 4).map((item, idx) => {
                // เงื่อนไขสำหรับ 3 รูป: สองรูปแรก grid-cols-2, รูปสุดท้าย grid-cols-1
                const isThreeItems = mediaItems.length === 3
                const colSpan = isThreeItems && idx === 2 ? 'col-span-2' : ''

                return (
                  <div
                    key={idx}
                    className={`relative group cursor-pointer ${colSpan}`}
                    onClick={() => {
                      setLightboxIndex(idx)
                      setLightboxOpen(true)
                    }}
                  >
                    {mediaItems.length > 4 && idx === 3 && (
                      <div className="absolute absolute-center text-5xl text-white bg-black/60 w-full h-full flex justify-center items-center rounded-lg">
                        <span>+ {mediaItems.length - 4}</span>
                      </div>
                    )}

                    {item.type === 'image' ? (
                      <img
                        src={item.url}
                        alt={`media-${idx}`}
                        className={
                          (mediaItems.length > 1 ? 'h-44' : '') +
                          ' w-full max-h-[1200px] object-cover rounded-lg border border-gray-200'
                        }
                        onError={(e) => {
                          ;(e.currentTarget as HTMLImageElement).src =
                            '/images/image-error.png'
                        }}
                      />
                    ) : (
                      <div
                        className={
                          (mediaItems.length > 1 ? 'h-44' : '') +
                          ' w-full max-h-[1200px] bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200 overflow-hidden'
                        }
                      >
                        <video
                          src={item.url}
                          className="w-full h-full object-cover rounded-lg"
                          controls={false}
                          preload="metadata"
                          onClick={(e) => e.preventDefault()}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-black/60 rounded-full p-2">
                            <svg
                              className="w-8 h-8 text-white opacity-80"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <polygon
                                points="8,5 19,12 8,19"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {/* LIGHTBOX DIALOG */}
          {mediaItems.length > 0 && (
            <LightboxDialog
              isOpen={lightboxOpen}
              onOpenChange={setLightboxOpen}
              mediaItems={mediaItems}
              initialIndex={lightboxIndex}
            />
          )}

          {feed.hashtags && feed.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {feed.hashtags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded hover:bg-blue-100 cursor-pointer transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
        {Array.isArray(feed?.latestLikers) && feed?.latestLikers.length > 0 && (
          <div
            className="flex items-center gap-4 mt-2 text-gray-600 text-xs cursor-pointer"
            onClick={() => setSelectedFeedId(feed.id)}
          >
            {(() => {
              const latestUser = feed?.latestLikers[0].user
              const name = latestUser
                ? latestUser.firstName
                  ? latestUser.firstName
                  : latestUser.displayName
                : ''
              return (
                <span>
                  {name
                    ? `${name.length > 23 ? name.slice(0, 23) + '...' : name}`
                    : ''}{' '}
                  {feed?.reactionCounts?.total &&
                  feed.reactionCounts.total - 1 > 0
                    ? `และอีก ${feed.reactionCounts.total - 1} คน ถูกใจโพสนี้`
                    : 'ถูกใจโพสนี้'}
                </span>
              )
            })()}
          </div>
        )}
        <div className="flex items-center gap-4 -mt-4">
          <Popover open={reactionOpen} onOpenChange={setReactionOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex px-0 items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                onClick={() => setReactionOpen((v) => !v)}
              >
                <div className="flex -space-x-1">
                  <div className="flex -space-x-2">
                    {activeReactions.map((type, index) => (
                      <Avatar
                        key={type}
                        className={`w-[27px] h-[27px] border-2 bg-transparent ${index > 0 ? 'ml-[1px]' : ''} ${
                          reacting ? 'scale-125 animate-bounce' : ''
                        }`}
                        style={{ zIndex: activeReactions.length - index }}
                      >
                        <AvatarFallback className="bg-gray-200">
                          {getReactionIcon(type, feed.currentUserReaction)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                  {activeReactions.length === 0 && (
                    <Heart
                      size={18}
                      className={`text-red-400 ml-2 transition-transform duration-300 ${
                        reacting ? 'scale-125 animate-bounce' : ''
                      }`}
                    />
                  )}
                </div>
                <span
                  className={`text-xs transition-transform duration-300 ${
                    reacting ? 'scale-125 animate-bounce' : ''
                  }`}
                >
                  {feed.reactionCounts?.total || 0}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="flex gap-2 p-2 bg-white border border-gray-200 rounded-lg shadow-lg w-full"
            >
              {reactions.map((r) => (
                <Button
                  key={r.type}
                  variant={reacting === r.type ? 'default' : 'ghost'}
                  size="icon"
                  className={`flex flex-col items-center justify-center w-10 h-10 transition-transform duration-300 ${
                    reacting === r.type ? 'scale-125 animate-bounce' : ''
                  }`}
                  onClick={async () => {
                    setReactionOpen(false)
                    await handleReact(feed.id, r.type)
                  }}
                  aria-label={r.label}
                >
                  {r.icon}
                </Button>
              ))}
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            onClick={() => {
              if (!feedId) router.push(paths.feedDetail(feed.id))
            }}
          >
            <MessageCircle size={18} className="text-blue-500" />
            <span className="text-xs">{feed.commentCount}</span>
          </Button>
          <Dialog
            open={open}
            onOpenChange={(e) => {
              setOpen(e)

              if (!e) {
                setShareId(null)
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() =>
                  setShareId(feed.sharedContent?.contentId || feed.id)
                }
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              >
                <Share2 size={18} className="text-green-500" />
                {!feed?.sharedContent && (
                  <span className="text-xs">{feed.shareCount}</span>
                )}
              </Button>
            </DialogTrigger>
            {!!shareId && (
              <FeedShareFormDialog
                feedId={shareId}
                onSuccess={() => {
                  if (reload) reload()

                  toast.success('แชร์โพสสำเร็จ')
                  setShareId(null)
                }}
                onError={() => {
                  toast.error('ไม่สามารถแชร์โพสได้')
                  setShareId(null)
                }}
              />
            )}
          </Dialog>
        </div>
      </Card>

      {/* ConfirmModal สำหรับลบโพสต์ */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="คุณต้องการลบโพสต์นี้หรือไม่?"
        description="การลบไม่สามารถย้อนกลับได้"
        confirmText="ลบโพสต์"
        cancelText="ยกเลิก"
        onConfirm={async () => {
          try {
            setIsDeleting(true)
            await axios.delete(
              endpoints.feeds.byId(
                feed.id,
                feed.userId !== user?.uid && user?.role === 'ADMIN',
              ),
            )
            toast.success('ลบโพสต์สำเร็จ')
            if (reload) reload()
          } catch {
            toast.error('เกิดข้อผิดพลาดในการลบโพสต์')
          } finally {
            setIsDeleting(false)
          }
        }}
        loading={isDeleting}
      />

      {!!SelectedFeedId && (
        <ReactionsListDialog
          feedId={SelectedFeedId}
          open={!!SelectedFeedId}
          onOpenChange={() => setSelectedFeedId(null)}
        />
      )}
    </>
  )
}
